import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const STORAGE_KEY = "tmm_cart_v1";
const API = process.env.REACT_APP_BACKEND_URL;

const mergeCarts = (local, server) => {
  const byId = new Map();
  [...server, ...local].forEach((it) => {
    const prev = byId.get(it.id);
    byId.set(it.id, prev ? { ...prev, qty: Math.max(prev.qty, it.qty) } : it);
  });
  return [...byId.values()];
};

export const CartProvider = ({ children }) => {
  const { token } = useAuth();
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const syncedRef = useRef(false);
  const pushTimerRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  // On login: pull the server cart, merge with local, push the result back.
  useEffect(() => {
    if (!token) {
      syncedRef.current = false;
      return;
    }
    let stop = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/cart`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        if (!res.ok || stop) return;
        const data = await res.json();
        setItems((local) => {
          const merged = mergeCarts(local, data.items || []);
          fetch(`${API}/api/cart`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            credentials: "include",
            body: JSON.stringify({ items: merged }),
          }).catch(() => {});
          return merged;
        });
        syncedRef.current = true;
      } catch {
        /* offline — local cart still works */
      }
    })();
    return () => {
      stop = true;
    };
  }, [token]);

  // Debounced push of cart changes to the server while logged in.
  useEffect(() => {
    if (!token || !syncedRef.current) return;
    clearTimeout(pushTimerRef.current);
    pushTimerRef.current = setTimeout(() => {
      fetch(`${API}/api/cart`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        credentials: "include",
        body: JSON.stringify({ items }),
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(pushTimerRef.current);
  }, [items, token]);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        return prev.map((p) =>
          p.id === product.id ? { ...p, qty: p.qty + qty } : p
        );
      }
      return [...prev, { ...product, qty }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((prev) =>
      prev
        .map((p) => (p.id === id ? { ...p, qty: Math.max(0, qty) } : p))
        .filter((p) => p.qty > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const subtotal = items.reduce((s, i) => s + i.qty * i.price, 0);
    return { count, subtotal };
  }, [items]);

  const value = useMemo(
    () => ({
      items,
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      toggleCart: () => setIsOpen((v) => !v),
      addItem,
      removeItem,
      setQty,
      clear,
      ...totals,
    }),
    [items, isOpen, addItem, removeItem, setQty, clear, totals]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
